import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Investor,
  InvestorListResponse,
  InvestorListParams,
  InvestorInput,
} from '@/lib/types';

// ----- API functions -----

export async function fetchInvestors(params: InvestorListParams): Promise<InvestorListResponse> {
  const { data } = await apiClient.get<InvestorListResponse>('/api/v1/investors', {
    params,
  });
  return data;
}

export async function fetchInvestor(id: string): Promise<Investor> {
  const { data } = await apiClient.get<{ investor: Investor }>(`/api/v1/investors/${id}`);
  return data.investor;
}

export async function createInvestor(input: InvestorInput): Promise<Investor> {
  const { data } = await apiClient.post<{ investor: Investor }>('/api/v1/investors', input);
  return data.investor;
}

export async function updateInvestor(id: string, input: Partial<InvestorInput>): Promise<Investor> {
  const { data } = await apiClient.patch<{ investor: Investor }>(`/api/v1/investors/${id}`, input);
  return data.investor;
}

// ----- React Query hooks -----

export function useInvestors(params: InvestorListParams) {
  return useQuery({
    queryKey: ['investors', params],
    queryFn: () => fetchInvestors(params),
    placeholderData: (prev) => prev, // smooth pagination
  });
}

export function useCreateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvestor,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investors'] });
    },
  });
}

export function useUpdateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<InvestorInput> }) =>
      updateInvestor(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['investors'] });
      qc.setQueryData(['investor', updated.id], updated);
    },
  });
}
